import { css } from '@emotion/react';
import { NotificationType } from '@weave/schema-gen-ts/dist/shared/notification/notifications.pb';
import dayjs from 'dayjs';
import { useTranslation } from '@frontend/i18n';
import { Icon } from '@frontend/icons';
import { GetWeavePopNotificationByType, NotificationActions } from '@frontend/types';
import { theme } from '@frontend/theme';
import { Text } from '@frontend/design-system';
import { useNotificationContext } from '../notification-provider';
import { BaseNotificationComponentProps, BaseNotificationInner, BaseNotificationOuter } from './base';

type Props = BaseNotificationComponentProps<GetWeavePopNotificationByType<'voicemail-message'>>;

export const VoicemailNotificationComponent = ({ isHistorical, notification, emit, ...rest }: Props) => {
  const { t } = useTranslation();

  const {
    voicemailMessageId,
    mailboxId,
    mailboxName,
    locationId,
    createdAt,
    personContact,
    callerNumber,
    transcriptionSnippet,
  } = notification.payload;

  const notificationTrayContext = useNotificationContext();

  const markRead = () =>
    notificationTrayContext.markVoicemailNotificationsAsRead(
      voicemailMessageId,
      mailboxId,
      NotificationType.VOICEMAIL_MESSAGE,
      locationId
    );

  const actions: NotificationActions = [
    {
      label: t('View'),
      action: () => {
        emit({ action: 'view', payload: undefined }, notification);
        markRead();
      },
    },
  ];

  return (
    <BaseNotificationOuter
      actions={actions}
      notification={notification}
      emit={emit}
      isHistorical={isHistorical}
      {...rest}
    >
      <BaseNotificationInner
        onClose={() => emit({ action: 'ignore', payload: undefined }, notification)}
        title={t(`New Voicemail`)}
        actions={actions}
        body={
          <section
            css={css`
              display: flex;
              flex-direction: column;
              align-items: flex-start;
              gap: ${theme.spacing(1)};
            `}
          >
            <Text
              weight='bold'
              size='medium'
              css={css`
                margin: 0;
              `}
            >
              {personContact || callerNumber}
            </Text>
            {!!transcriptionSnippet && (
              <Text
                weight='regular'
                size='medium'
                css={css`
                  margin: 0;
                `}
              >
                <i>
                  <q>{transcriptionSnippet}</q>
                </i>
              </Text>
            )}
            <Text
              size='medium'
              css={css`
                margin: 0;
              `}
            >
              {dayjs(createdAt).format('MMM DD YYYY, hh:mm A')}
            </Text>

            <div
              css={css`
                display: flex;
                gap: ${theme.spacing(0.5)};
                align-items: center;
              `}
            >
              <Icon name='voicemail-small' />
              <Text
                size='medium'
                css={css`
                  margin: 0;
                `}
              >
                {mailboxName}
              </Text>
            </div>
          </section>
        }
        id={notification.id}
        location={notification.location}
        timestamp={notification.timestamp}
        notificationType={notification.type}
        {...rest}
      ></BaseNotificationInner>
    </BaseNotificationOuter>
  );
};
